import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import moment, { Moment } from 'moment';
import { ORDER_METHODS } from '../../services/exports/Constants';
import { StoreInterface } from '../../store/types';
import { CompanyDetailsInterface, ScheduledOrderSlotInterface } from '../../services/exports/Interfaces';
import { useGetBusySlotsQuery } from '../../store/api-slices';
import useCompanyBusinessTime from './company-business-time/useCompanyBusinessTime';

dayjs.extend(utc);
dayjs.extend(timezone);

interface OrderSlotsProps {
  orderMethod: ORDER_METHODS;
  date?: Moment;
  disabled?: boolean;
  company?: CompanyDetailsInterface;
}

const useOrderSlots = (props: OrderSlotsProps) => {
  const defaultCompany = useSelector((store: StoreInterface) => store.initialData.company);
  const company = props?.company ?? defaultCompany;
  const {
    isLoading: isCompanyScheduleLoading,
    businessTimes,
    holidays,
    isWithinMaxPreOrderDays,
    isBusinessDay,
    getNextBusinessTime,
  } = useCompanyBusinessTime({
    company,
    orderMethod: props?.orderMethod,
    withTimeOffsets: true,
  });
  const now = dayjs();
  const minScheduleAheadDelivery = company?.min_schedule_ahead_time_delivery ?? 0;
  const minScheduleAheadPickup = company?.min_schedule_ahead_time_pickup ?? 0;
  const scheduleAheadTime =
    props.orderMethod === ORDER_METHODS.DELIVERY ? minScheduleAheadDelivery : minScheduleAheadPickup;
  const timeSlotInterval = company?.scheduled_orders_time_slot_interval ?? 0;
  const normalizedDate = useMemo(() => {
    const isToday = props.date && props.date.isSame(now.toISOString(), 'day');

    if (!props.date || isToday || !isWithinMaxPreOrderDays(dayjs(props.date.toISOString()))) {
      return now.clone().add(scheduleAheadTime, 'minute');
    }

    return dayjs(props.date.toISOString()).startOf('day');
  }, [props.date, scheduleAheadTime]);
  const updateSlotsDatePattern = normalizedDate.format('YYYY-MM-DD');
  const companyId = company?.id;
  const skipGetBusySlotsQuery = props.disabled || !companyId || !props.orderMethod;
  const { data: busySlots, isLoading: isBusySlotsLoading } = useGetBusySlotsQuery(
    {
      companyId,
      params: { order_method: props.orderMethod, date: updateSlotsDatePattern },
    },
    { skip: skipGetBusySlotsQuery, refetchOnFocus: true },
  );

  const orderSlots = useMemo((): ScheduledOrderSlotInterface[] => {
    if (!busySlots || isCompanyScheduleLoading || props.disabled) {
      return [];
    }

    const startBase = normalizedDate.toISOString();
    const businessDay = isBusinessDay(startBase) ? normalizedDate : dayjs(getNextBusinessTime(startBase));
    const dayKey = businessDay.format('dddd').toLowerCase();
    const intervals = businessTimes[dayKey] || [];
    const slots: ScheduledOrderSlotInterface[] = [];

    intervals.forEach(({ start, end }) => {
      const startTimeline = moment(start, 'HH:mm:ss');
      const roundedMinutes = Math.ceil(startTimeline.minutes() / 5) * 5;

      const endTimeline = moment(end, 'HH:mm:ss');
      const endTime = businessDay
        .clone()
        .hour(endTimeline.hour())
        .minute(endTimeline.minute())
        .second(0)
        .millisecond(0);
      let cursor = businessDay.clone().hour(startTimeline.hour()).minute(roundedMinutes).second(0).millisecond(0);

      while (cursor.isBefore(endTime)) {
        const nextCursor = cursor.clone().add(timeSlotInterval, 'minute');
        const isBusySlot = busySlots[cursor.clone().utc().format()];
        const isValidSlot = cursor.isAfter(businessDay) && !isBusySlot;

        if (isValidSlot) {
          slots.push({
            start_time: cursor.toISOString(),
            end_time: nextCursor.toISOString(),
          });
        }

        cursor = nextCursor;
      }
    });

    return slots;
  }, [
    businessTimes,
    holidays,
    normalizedDate.valueOf(),
    isCompanyScheduleLoading,
    timeSlotInterval,
    busySlots,
    updateSlotsDatePattern,
    props.disabled,
  ]);

  const isLoading = isCompanyScheduleLoading || isBusySlotsLoading;

  return {
    orderSlots,
    isLoading,
  };
};

export default useOrderSlots;
